import { FiPlus } from "react-icons/fi";
import {
  SmallInputField,
  BoxSelector,
  InputWithUnit,
  CheckboxField,
  MiniPdfUploadSection,
} from "./index.js";
import { AREA_UNITS } from "../../../../utils/constants.js";

const FloorPlanCard = ({
  unitData,
  onUpdate,
  onRemove,
  index,
  totalUnits,
  allowMultiple,
}) => {
  const handleChange = (field, value) => {
    onUpdate(index, { ...unitData, [field]: value });
  };
  const handleCarpetAreaChange = (field, value) => {
    onUpdate(index, {
      ...unitData,
      carpetArea: {
        ...unitData.carpetArea,
        [field]: value,
      },
    });
  };
  const handleConditionsChange = (field, value) => {
    onUpdate(index, {
      ...unitData,
      conditions: {
        ...unitData.conditions,
        [field]: value,
      },
    });
  };

  return (
    <div className="space-y-4 select-none">
      {allowMultiple && (
        <div className="flex justify-between items-center pb-3 border-b border-border">
          <h3 className="text-sm font-semibold text-text-primary font-poppins">
            Unit {index + 1}
          </h3>

          {totalUnits > 1 && (
            <button
              type="button"
              onClick={onRemove}
              className="text-sm font-medium text-btn-warning hover:text-btn-brandRed transition-colors"
            >
              Remove
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
        <BoxSelector
          label="In BHK"
          value={unitData?.bhk}
          onChange={(val) => handleChange("bhk", val)}
          totalBoxes={4}
        />

        <BoxSelector
          label="No. of Bathrooms"
          value={unitData?.bathrooms}
          onChange={(val) => handleChange("bathrooms", val)}
          totalBoxes={4}
        />

        <BoxSelector
          label="No. of Balcony"
          value={unitData?.balcony}
          onChange={(val) => handleChange("balcony", val)}
          totalBoxes={4}
        />

        <MiniPdfUploadSection
          floorDesign={unitData?.floorDesign}
          onChange={(pdfUrl) => handleChange("floorDesign", pdfUrl)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {allowMultiple && (
          <SmallInputField
            label="Floor Number"
            name="floorNumber"
            value={unitData?.floorNumber === 0 ? null : unitData?.floorNumber}
            onChange={(e) =>
              handleChange("floorNumber", Number(e.target.value))
            }
            placeholder="0 for Ground , 1 , 2 .."
          />
        )}
        <div className={`${allowMultiple ? "" : "col-span-2"}`}>
          <InputWithUnit
            type="number"
            label="Carpet Area"
            name="carpetArea"
            value={unitData?.carpetArea?.value}
            selectedUnit={unitData?.carpetArea?.unit}
            onChange={(e) =>
              handleCarpetAreaChange("value", Number(e.target.value))
            }
            onUnitChange={(unit) => handleCarpetAreaChange("unit", unit)}
            placeholder="Enter area"
            units={AREA_UNITS}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SmallInputField
          type="number"
          label="Price per sqft."
          name="pricePerSqft"
          value={unitData?.pricePerSqft}
          onChange={(e) => handleChange("pricePerSqft", Number(e.target.value))}
          placeholder="₹ Price"
        />
        <SmallInputField
          type="number"
          label="Expected Price"
          name="expectedPrice"
          value={unitData?.expectedPrice}
          onChange={(e) =>
            handleChange("expectedPrice", Number(e.target.value))
          }
          placeholder="₹ Expected Price"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <CheckboxField
          label="All inclusive price"
          checked={unitData?.conditions?.allInclusivePrice}
          onChange={(val) => handleConditionsChange("allInclusivePrice", val)}
        />
        <CheckboxField
          label="Price Negotiable"
          checked={unitData?.conditions?.priceNegotiable}
          onChange={(val) => handleConditionsChange("priceNegotiable", val)}
        />
        <div className="sm:col-span-2">
          <CheckboxField
            label="Tax and Govt. charges excluded"
            checked={unitData?.conditions?.taxExcluded}
            onChange={(val) => handleConditionsChange("taxExcluded", val)}
          />
        </div>
      </div>
    </div>
  );
};
const FloorPlan = ({ floorPlan, onChange, allowMultiple = true }) => {
  // console.log("floorplan:", floorPlan);
  const initialData = {
    floorNumber: 0,
    bhk: 2,
    bathrooms: 2,
    balcony: 1,
    floorDesign: "",
    carpetArea: {
      value: "",
      unit: "sqft.",
    },
    pricePerSqft: "",
    expectedPrice: "",
    conditions: {
      allInclusivePrice: false,
      taxExcluded: false,
      priceNegotiable: false,
    },
  };
  const normalize = (data) =>
    !data || (Array.isArray(data) && data?.length === 0)
      ? [initialData]
      : Array.isArray(data)
      ? data
      : [data];

  const units = normalize(floorPlan);

  const handleAddFloor = () => {
    onChange([...units, normalize()[0]]);
  };

  const handleUpdateFloor = (index, updatedData) => {
    const cloned = [...units];
    cloned[index] = updatedData;
    onChange(cloned);
  };

  const handleRemoveFloor = (index) => {
    if (units.length > 1) {
      onChange(units.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="flex flex-col gap-6 -m-2">
      <div className="space-y-6 mt-4">
        {units.map((unit, index) => (
          <div
            key={index}
            className="p-4 border border-border rounded-lg bg-background-card"
          >
            <FloorPlanCard
              index={index}
              allowMultiple={allowMultiple}
              unitData={unit}
              onUpdate={handleUpdateFloor}
              onRemove={() => handleRemoveFloor(index)}
              totalUnits={units.length}
            />
          </div>
        ))}
      </div>

      {allowMultiple && (
        <button
          type="button"
          onClick={handleAddFloor}
          className="border border-primary text-primary px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-primary hover:bg-opacity-30 transition-all text-sm w-full"
        >
          <FiPlus className="w-4 h-4" />
          Add More Unit
        </button>
      )}
    </div>
  );
};

export default FloorPlan;

// import { FiPlus } from "react-icons/fi";
// import {
//   SmallInputField,
//   BoxSelector,
//   InputWithUnit,
//   CheckboxField,
//   MiniPdfUploadSection,
// } from "./index.js";
// import { useEffect, useState } from "react";
// import { AREA_UNITS } from "../../../../utils/constants.js";

// const FloorPlanCard = ({
//   unitData,
//   onUpdate,
//   onRemove,
//   index,
//   totalUnits,
//   allowMultiple,
// }) => {
//   const handleChange = (field, value) => {
//     onUpdate(index, { ...unitData, [field]: value });
//   };
//   // console.log("unitDatat", unitData);

//   return (
//     <div className="space-y-4 select-none">
//       {/* Floor Identifier */}
//       {allowMultiple && (
//         <div className="flex justify-between items-center pb-3 border-b border-border">
//           <h3 className="text-sm font-semibold text-text-primary font-poppins">
//             Unit {index + 1}
//           </h3>

//           {totalUnits > 1 && (
//             <button
//               type="button"
//               onClick={onRemove}
//               className="text-sm font-medium text-btn-warning hover:text-btn-brandRed transition-colors"
//             >
//               Remove
//             </button>
//           )}
//         </div>
//       )}

//       <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
//         <BoxSelector
//           label="In BHK"
//           value={unitData?.bhk}
//           onChange={(val) => handleChange("bhk", val)}
//           totalBoxes={4}
//         />

//         <BoxSelector
//           label="No. of Bathrooms"
//           value={unitData?.bathrooms}
//           onChange={(val) => handleChange("bathrooms", val)}
//           totalBoxes={4}
//         />

//         <BoxSelector
//           label="No. of Balcony"
//           value={unitData?.balcony}
//           onChange={(val) => handleChange("balcony", val)}
//           totalBoxes={4}
//         />

//         {/* PDF Upload / Preview Section */}
//         <MiniPdfUploadSection
//           floorDesign={unitData?.floorDesign}
//           onChange={(pdfUrl) => handleChange("floorDesign", pdfUrl)}
//         />
//       </div>

//       <div className="grid grid-cols-1 sm:grid-cols-2  gap-4">
//         {allowMultiple && (
//           <SmallInputField
//             label="Floor Number"
//             name="floorNumber"
//             value={unitData?.floorNumber === 0 ? null : unitData?.floorNumber}
//             onChange={(e) =>
//               handleChange("floorNumber", Number(e.target.value))
//             }
//             placeholder="0 for Ground , 1 , 2 .."
//           />
//         )}
//         <div className={`${allowMultiple ? "" : "col-span-2"}`}>
//           <InputWithUnit
//             type="number"
//             label="Carpet Area"
//             name="carpetArea"
//             value={unitData?.carpetArea}
//             onChange={(e) => handleChange("carpetArea", Number(e.target.value))}
//             onUnitChange={(unit) => handleChange("carpetAreaUnit", unit)}
//             placeholder="Enter area"
//             units={AREA_UNITS}
//           />
//         </div>
//         <SmallInputField
//           type="number"
//           label="Price per sqft."
//           name="pricePerSqft"
//           value={unitData?.pricePerSqft}
//           onChange={(e) => handleChange("pricePerSqft", Number(e.target.value))}
//           placeholder="₹ Price"
//         />
//         <SmallInputField
//           type="number"
//           label="Expected Price"
//           name="expectedPrice"
//           value={unitData?.expectedPrice}
//           onChange={(e) =>
//             handleChange("expectedPrice", Number(e.target.value))
//           }
//           placeholder="₹ Expected Price"
//         />
//       </div>

//       <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
//         <CheckboxField
//           label="All inclusive price"
//           checked={unitData?.allInclusivePrice}
//           onChange={(val) => handleChange("allInclusivePrice", val)}
//         />
//         <CheckboxField
//           label="Price Negotiable"
//           checked={unitData?.priceNegotiable}
//           onChange={(val) => handleChange("priceNegotiable", val)}
//         />
//         <div className="sm:col-span-2">
//           <CheckboxField
//             label="Tax and Govt. charges excluded"
//             checked={unitData?.taxExcluded}
//             onChange={(val) => handleChange("taxExcluded", val)}
//           />
//         </div>
//       </div>
//     </div>
//   );
// };

// const FloorPlan = ({ floorPlan, onChange, allowMultiple = true }) => {
//   // console.log("floorPlan", floorPlan);

//   // Handle both array and object formats
//   const getInitialUnits = () => {
//     if (!floorPlan) {
//       return [
//         {
//           floorNumber: 0,
//           bhk: 2,
//           bathrooms: 2,
//           balcony: 1,
//           floorDesign: "",
//           carpetArea: "",
//           carpetAreaUnit: "sqft.",
//           pricePerSqft: "",
//           expectedPrice: "",
//           allInclusivePrice: true,
//           taxExcluded: false,
//           priceNegotiable: true,
//         },
//       ];
//     }

//     // If floorPlan is an object, wrap it in an array
//     if (!Array.isArray(floorPlan)) {
//       return [floorPlan];
//     }

//     // If it's already an array, return as is
//     return floorPlan;
//   };

//   const [units, setUnits] = useState(getInitialUnits());

//   // notify parent on state change
//   useEffect(() => {
//     onChange(units);
//   }, [units]);

//   const handleAddFloor = () => {
//     setUnits([
//       ...units,
//       {
//         floorNumber: 0,
//         bhk: 2,
//         bathrooms: 2,
//         balcony: 1,
//         carpetArea: "",
//         carpetAreaUnit: "sqft.",
//         pricePerSqft: "",
//         floorDesign: "",
//         expectedPrice: "",
//         allInclusivePrice: true,
//         taxExcluded: false,
//         priceNegotiable: true,
//       },
//     ]);
//   };

//   const handleUpdateFloor = (index, updatedData) => {
//     const newFloors = [...units];
//     newFloors[index] = updatedData;
//     setUnits(newFloors);
//   };

//   const handleRemoveFloor = (index) => {
//     if (units.length > 1) {
//       setUnits(units.filter((_, i) => i !== index));
//     }
//   };

//   return (
//     <div className="flex flex-col gap-6 -m-2">
//       <div className="space-y-6 mt-4">
//         {units.map((unit, index) => (
//           <div
//             key={index}
//             className="p-4 border border-border rounded-lg bg-background-card"
//           >
//             <FloorPlanCard
//               index={index}
//               allowMultiple={allowMultiple}
//               unitData={unit}
//               onUpdate={handleUpdateFloor}
//               onRemove={() => handleRemoveFloor(index)}
//               totalUnits={units.length}
//             />
//           </div>
//         ))}
//       </div>

//       {allowMultiple && (
//         <button
//           type="button"
//           onClick={handleAddFloor}
//           className="border border-primary text-primary px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-primary hover:bg-opacity-30 transition-all text-sm w-full"
//         >
//           <FiPlus className="w-4 h-4" />
//           Add More Unit
//         </button>
//       )}
//     </div>
//   );
// };
// export default FloorPlan;
